import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ImageIcon, X } from 'lucide-react'

/**
 * The form's header is the product itself — thumbnail, live name, category
 * and price — so the operator confirms they opened the right item before
 * scrolling. "Edit product" becomes a small eyebrow instead of the heading.
 */
export function ProductIdentityHeader({
  mode,
  name,
  imageUrl,
  categoryName,
  priceLabel,
  trackedInInventory,
  onClose,
  closeDisabled,
}: {
  mode: 'create' | 'edit'
  name: string
  imageUrl: string
  categoryName?: string
  priceLabel: string
  trackedInInventory: boolean
  onClose?: () => void
  closeDisabled?: boolean
}) {
  const showImage = imageUrl.startsWith('http') || imageUrl.startsWith('data:image')
  const details = [categoryName, priceLabel].filter(Boolean)
  return (
    <div className="flex items-center gap-4 border-b px-6 py-4">
      {showImage ? (
        <img src={imageUrl} alt="" className="h-14 w-14 shrink-0 rounded-lg border object-cover" />
      ) : (
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border bg-muted text-muted-foreground">
          <ImageIcon className="h-5 w-5" aria-hidden />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{mode === 'edit' ? 'Edit product' : 'New product'}</p>
        <h2 className="truncate text-lg font-semibold leading-tight">{name.trim() || 'Untitled product'}</h2>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
          <span className="truncate">{details.join(' · ')}</span>
          {trackedInInventory && (
            <Badge variant="success-surface" className="font-medium">
              Tracked in inventory
            </Badge>
          )}
        </div>
      </div>
      {onClose && (
        <Button type="button" variant="ghost" size="icon" onClick={onClose} disabled={closeDisabled} aria-label="Close">
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
